"use client";

import { MessageCircle, Newspaper, Sparkles, User } from "lucide-react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { useAuthState } from "@/hooks/use-auth-state";
import { buildOnboardingURL } from "@/lib/auth-redirect";
import { useAuthPrompt } from "./auth-prompt-provider";

const ICON_MAP = {
  sparkles: Sparkles,
  feed: Newspaper,
  profile: User,
  chat: MessageCircle,
};

interface AuthPromptCardProps {
  title: string;
  description: string;
  redirectTo: string;
  iconName?: keyof typeof ICON_MAP;
  /**
   * Whether the viewer already has a session. Callers gate this card on
   * "signed in AND onboarded", so a signed-in viewer who still sees it is
   * someone who bailed out of onboarding ("Explore first") — the sign-in
   * drawer is a no-op for them, and the card has to send them back to finish
   * their profile instead. Server callers pass what they already computed;
   * client callers fall back to the shared session query.
   */
  hasSession?: boolean;
}

export function AuthPromptCard({
  title,
  description,
  redirectTo,
  iconName = "sparkles",
  hasSession,
}: AuthPromptCardProps) {
  const { openAuthPrompt } = useAuthPrompt();
  const { authStatus } = useAuthState();
  const router = useRouter();
  const Icon = ICON_MAP[iconName] ?? Sparkles;

  const needsOnboarding = hasSession ?? authStatus === "authenticated";
  // "Sign in for Chats" -> "chats"
  const featureLabel = title.replace(/^sign in for /i, "").toLowerCase();

  const heading = needsOnboarding ? "Finish your profile" : title;
  const body = needsOnboarding
    ? `You're signed in — finish setting up your profile to unlock ${featureLabel}.`
    : description;

  const handleClick = () => {
    if (needsOnboarding) {
      router.push(buildOnboardingURL(redirectTo));
      return;
    }
    openAuthPrompt({
      title: "Sign in to continue",
      description: `Sign in to access your ${featureLabel} instantly.`,
      callbackURL: redirectTo,
    });
  };

  return (
    <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-glass-border-10 bg-neutral-950/40 p-8 text-center shadow-[inset_0_1px_1px_rgba(255,255,255,0.15),0_12px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-all duration-300 hover:border-glass-border-20 hover:shadow-[inset_0_1px_2px_rgba(255,255,255,0.25),0_16px_48px_rgba(0,0,0,0.6)]">
      {/* Liquid Glass Background Glows */}
      <div className="pointer-events-none absolute -top-12 -left-12 h-36 w-36 rounded-full bg-brand/10 blur-2xl transition-opacity duration-300" />
      <div className="pointer-events-none absolute -right-12 -bottom-12 h-36 w-36 rounded-full bg-brand/15 blur-2xl transition-opacity duration-300" />

      {/* Decorative inner gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

      <div className="relative z-10 flex flex-col items-center">
        {/* Modern, glossy icon container */}
        <div className="relative mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-glass-border-10 bg-glass-bg-05 text-brand shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] transition-all duration-500 hover:scale-110 hover:rotate-6 hover:border-brand/40 hover:text-brand/90">
          <Icon className="h-6 w-6" />
        </div>

        <h3 className="mb-3 text-xl font-semibold tracking-tight text-text-primary">
          {heading}
        </h3>

        <p className="mx-auto mb-6 max-w-xs text-sm leading-relaxed text-text-secondary">
          {body}
        </p>

        <Button
          type="button"
          // The `dark:` copies of the brand background are load-bearing: the
          // default Button variant sets `dark:bg-white/[0.08]`, and
          // tailwind-merge only resolves conflicts within the same modifier, so
          // an unmodified `bg-brand` loses to it under the app's default dark
          // theme and this CTA rendered as a neutral glass button.
          className="w-full max-w-xs rounded-xl bg-brand py-6 font-semibold text-brand-foreground shadow-[0_4px_20px_rgba(106,51,255,0.3)] transition-all duration-200 hover:scale-[1.02] hover:bg-brand/90 hover:shadow-[0_6px_24px_rgba(106,51,255,0.45)] active:scale-[0.98] dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
          onClick={handleClick}
        >
          {needsOnboarding ? "Continue setup" : "Sign in to continue"}
        </Button>
      </div>
    </div>
  );
}
